import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { common, financedEmissions } from "../../../i18n";
import SectorMultiSelect from "../../../components/cat15/SectorMultiSelect";
import { getReportData, getSectors } from "../../../service/cat15.service";
import { ReportData, ReportFormat } from "../../../models/dto/cat15/cat15.types";
import { SectorOption } from "../../../models/dto/cat15/cat15.api";
import ReportPreview from "./ReportPreview";
import ProgressModal from "./ProgressModal";

const YEAR_OPTIONS = [2025, 2024, 2023];
// ISIC is the borrower's primary sector taxonomy, so the report's Sector Breakdown uses ISIC.
const TAXONOMY = "isic" as const;

const GenerateTab = () => {
  const { t, i18n } = useTranslation([financedEmissions, common], {
    nsMode: "fallback",
  });

  const [year, setYear] = useState(2025);
  const [excel, setExcel] = useState(true);
  const [pdf, setPdf] = useState(true);
  const [sectors, setSectors] = useState<string[]>([]);
  const [sectorList, setSectorList] = useState<SectorOption[]>([]);
  const [data, setData] = useState<ReportData | undefined>();
  const [openProgress, setOpenProgress] = useState(false);

  // Level-1 taxonomy sections for the filter (values are section codes — what BE filters on).
  useEffect(() => {
    let active = true;
    getSectors(TAXONOMY)
      .then((s) => active && setSectorList(s))
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, []);

  // The preview reflects the server-side sector filter, so it re-fetches when year or sectors change.
  useEffect(() => {
    let active = true;
    setData(undefined);
    getReportData(year, TAXONOMY, sectors)
      .then((d) => {
        if (active) setData(d);
      })
      .catch(() => undefined); // handleApiError already surfaced the alert
    return () => {
      active = false;
    };
  }, [year, sectors]);

  const formats = [
    excel ? ("excel" as const) : null,
    pdf ? ("pdf" as const) : null,
  ].filter(Boolean) as ReportFormat[];

  const isThai = i18n.language?.startsWith("th");
  const sectorOptions = useMemo(
    () => sectorList.map((s) => ({ value: s.value, label: isThai ? s.labelTh : s.labelEn })),
    [sectorList, isThai],
  );

  return (
    <Box display="flex" flexDirection="column" gap="20px">
      <Card variant="outlined" sx={{ borderRadius: "8px" }}>
        <CardContent sx={{ p: "20px", "&:last-child": { pb: "20px" } }}>
          <Box display="flex" flexWrap="wrap" alignItems="flex-end" gap="24px">
            <Box minWidth="180px">
              <FormControl fullWidth size="small">
                <InputLabel id="report-year-label" shrink>
                  {t("Filter-Reporting-Year")}
                </InputLabel>
                <Select
                  labelId="report-year-label"
                  label={t("Filter-Reporting-Year")}
                  value={String(year)}
                  onChange={(e) => setYear(Number(e.target.value))}
                >
                  {YEAR_OPTIONS.map((y) => (
                    <MenuItem key={y} value={String(y)}>
                      {y}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box minWidth="240px" flex={1} maxWidth="360px">
              <SectorMultiSelect
                label={t("Filter-Sector")}
                options={sectorOptions}
                value={sectors}
                onChange={setSectors}
                idKey="report-sector"
              />
            </Box>

            <Box>
              <Typography variant="body2" color="var(--gray9)" mb="4px">
                {t("Report-Output-Format")}
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={excel}
                    onChange={(e) => setExcel(e.target.checked)}
                  />
                }
                label={t("Format-Excel")}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={pdf}
                    onChange={(e) => setPdf(e.target.checked)}
                  />
                }
                label={t("Format-PDF")}
              />
            </Box>
            <Button
              variant="contained"
              color="secondary"
              disabled={formats.length === 0}
              onClick={() => setOpenProgress(true)}
              sx={{ ml: "auto" }}
            >
              {t("Generate-Report")}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {data && <ReportPreview data={data} />}

      <ProgressModal
        open={openProgress}
        onClose={() => setOpenProgress(false)}
        year={year}
        formats={formats}
        sectors={sectors}
      />
    </Box>
  );
};

export default GenerateTab;
